import React, { FunctionComponent } from "react";
import { makeStyles } from "tss-react/mui";
import {
    Button,
    CircularProgress,
    Container,
    CssBaseline,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Theme,
    Tooltip,
    Typography,
} from "@mui/material";
import * as Yup from "yup";
import { RootStateOrAny, useSelector } from "react-redux";
import { useAppDispatch } from "@/Redux/Redux";
import { useDisplayMessage } from "@/Utils/CustomHooks/useDisplayMessage";
import logo from "@/Style/crentweb-logo-black.svg";
import { FormProvider, useForm } from "react-hook-form";
import InputText from "@/Components/FormComponents/InputText/InputText";
import { yupResolver } from "@hookform/resolvers/yup";
import { getLocale, selectAppCode } from "@/Redux/App/AppReducer";
import { changeAppCode, changeLocale } from "@/Redux/App/AppActions";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";

interface LoginFormProps {}

const useStyles = makeStyles()((theme: Theme) => ({
    paper: {
        marginTop: theme.spacing(8),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },

    avatar: {
        margin: theme.spacing(1),
        width: 200,
        paddingBottom: 15,
    },

    form: {
        width: "100%", // Fix IE 11 issue.
        marginTop: theme.spacing(1),
    },

    submit: {
        margin: theme.spacing(0, 0, 2),
    },

    buttonProgress: {
        position: "absolute",
        top: "54%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },

    wrapper: {
        marginTop: theme.spacing(1),
        position: "relative",
    },

    error: {
        color: theme.palette.error.main,
    },
}));

const WebsiteAuthcodeForm: FunctionComponent<
    LoginFormProps
> = (): JSX.Element => {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();
    const language = useSelector((state) => getLocale());
    const appCode = useSelector((state) =>
        selectAppCode(state as RootStateOrAny),
    );
    const history = useHistory();

    const { displayMessage } = useDisplayMessage();
    const { t } = useTranslation();

    const languageItems = [
        { id: "de", name: "Deutsch" },
        { id: "us", name: "English" },
        { id: "es", name: "Español" },
        { id: "nl", name: "Niederländisch" },
    ];

    const methods = useForm({
        mode: "onSubmit",
        defaultValues: {
            appCode: appCode,
        },
        resolver: yupResolver(
            Yup.object({
                appCode: Yup.string().required(
                    "Dieses Feld muss ausgeüllt werden",
                ),
            }),
        ),
    });

    //@ts-ignore
    const onSubmit = (data) => {
        return new Promise((resolve, reject) => {
            submitAppCode(data.appCode, () => {
                resolve(true);
            });
        });
    };

    const submitAppCode = (appCode: string, onSubmitted: Function) => {
        dispatch(
            changeAppCode(appCode, (err) => {
                if (err) {
                    displayMessage("error", t("messages.login_failure"));
                }
                onSubmitted();
            }),
        );
    };

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline />
            <FormControl margin={"dense"}>
                <InputLabel id={"language-label"}>
                    {t("login.language")}
                </InputLabel>
                <Select
                    size={"small"}
                    labelId={"language-label"}
                    label={t("login.language")}
                    disabled={methods.formState.isSubmitting}
                    margin={"dense"}
                    value={language}
                    onChange={(event) => {
                        //onChange(event);
                        dispatch(changeLocale(event.target.value, (err) => {}));
                    }}
                >
                    <MenuItem value={"-1"}>
                        {t("general.selection.selectOption")}
                    </MenuItem>
                    {languageItems.map((item: any) => (
                        <MenuItem value={item.id}>{item.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <div className={classes.paper}>
                <img className={classes.avatar} src={logo} alt={"crw-logo"} />
                <Typography component="h1" variant="h5">
                    {t("login.headline")}
                </Typography>
                <FormProvider {...methods}>
                    <form
                        className={classes.form}
                        onSubmit={methods.handleSubmit((data) =>
                            onSubmit(data),
                        )}
                        noValidate
                    >
                        <InputText
                            name={"appCode"}
                            label={t("login.appCode")}
                            disabled={!_.isEmpty(appCode)}
                        />

                        <div className={classes.wrapper}>
                            <Tooltip title={t("login.tooltip.login")}>
                                <Button
                                    type={"submit"}
                                    size={"small"}
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    className={classes.submit}
                                    disabled={methods.formState.isSubmitting}
                                >
                                    {t("login.button")}
                                </Button>
                            </Tooltip>
                            {methods.formState.isSubmitting && (
                                <CircularProgress
                                    size={24}
                                    className={classes.buttonProgress}
                                />
                            )}
                        </div>
                    </form>
                </FormProvider>
            </div>
        </Container>
    );
};

export default WebsiteAuthcodeForm;
